import { apiRequest } from './api';

const API_PREFIX = '/technical-maintenance';

const buildQuery = (params = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      queryParams.append(key, value);
    }
  });
  const query = queryParams.toString();
  return query ? `?${query}` : '';
};

/** Django APPEND_SLASH: action/list URL'leri sondaki / ile biter, query hemen sonra gelir. */
const actionUrl = (path, params = {}) => {
  const normalized = path.endsWith('/') ? path : `${path}/`;
  return `${normalized}${buildQuery(params)}`;
};

const isNotFoundError = (err) => {
  const msg = String(err?.message || '');
  return msg.includes('404') || msg.toLowerCase().includes('not found');
};

const unwrapList = (response) => response?.data ?? response ?? [];

const buildEquipmentDetailPayload = (equipment, records) => {
  const sorted = [...records].sort((a, b) => {
    const da = a.completed_date || a.scheduled_date || '';
    const db = b.completed_date || b.scheduled_date || '';
    return String(db).localeCompare(String(da));
  });
  const completed = sorted.filter((row) => row.status === 'completed');
  const latestCompleted = completed[0];
  const totalCost = sorted.reduce((sum, row) => sum + (Number(row.cost) || 0), 0);

  return {
    equipment,
    maintenance_records: sorted,
    stats: {
      total_maintenance_count: sorted.length,
      completed_count: completed.length,
      total_cost: totalCost,
      latest_completed_date: latestCompleted?.completed_date || null,
      next_maintenance_date: latestCompleted?.next_maintenance_date || null,
    },
  };
};

const buildSchedulePayload = (records, params = {}) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysAhead = Math.min(Math.max(Number(params.days_ahead) || 90, 1), 365);
  const area = params.area || '';

  let filtered = records;
  if (area) {
    filtered = filtered.filter((row) => row.equipment_area === area);
  }

  const overdue = [];
  const upcoming = [];
  const planned = [];

  filtered.forEach((record) => {
    if (record.status === 'planned' && record.scheduled_date) {
      const scheduleDate = new Date(`${record.scheduled_date}T00:00:00`);
      planned.push({
        ...record,
        schedule_kind: 'planned',
        schedule_date: record.scheduled_date,
        days_until: Math.round((scheduleDate - today) / 86400000),
      });
      return;
    }

    if (!record.next_maintenance_date) return;

    const scheduleDate = new Date(`${record.next_maintenance_date}T00:00:00`);
    const daysUntil = Math.round((scheduleDate - today) / 86400000);
    const item = {
      ...record,
      schedule_kind: 'next_maintenance',
      schedule_date: record.next_maintenance_date,
      days_until: daysUntil,
    };

    if (daysUntil < 0) overdue.push(item);
    else if (daysUntil <= daysAhead) upcoming.push(item);
  });

  const byDate = (a, b) => String(a.schedule_date).localeCompare(String(b.schedule_date));
  overdue.sort(byDate);
  upcoming.sort(byDate);
  planned.sort(byDate);

  return {
    today: today.toISOString().slice(0, 10),
    days_ahead: daysAhead,
    overdue_count: overdue.length,
    upcoming_count: upcoming.length,
    planned_count: planned.length,
    overdue,
    upcoming,
    planned,
  };
};

const technicalMaintenanceApi = {
  getModuleAccessUsers: async () => apiRequest(`${API_PREFIX}/module-access/`),
  updateModuleAccessUsers: async (userIds) =>
    apiRequest(`${API_PREFIX}/module-access/`, 'PUT', { user_ids: userIds }),

  getDashboardStats: async (params = {}) =>
    apiRequest(actionUrl(`${API_PREFIX}/equipment/dashboard-stats`, params)),

  getTopMaintenanceEquipment: async (params = {}) =>
    apiRequest(actionUrl(`${API_PREFIX}/equipment/top-maintenance-equipment`, params)),

  getEquipment: async (params = {}) =>
    apiRequest(`${API_PREFIX}/equipment/${buildQuery(params)}`),
  getEquipmentFilterOptions: async () =>
    apiRequest(`${API_PREFIX}/equipment/filter-options/`),
  createEquipment: async (data) =>
    apiRequest(`${API_PREFIX}/equipment/`, 'POST', data),
  updateEquipment: async (id, data) =>
    apiRequest(`${API_PREFIX}/equipment/${id}/`, 'PATCH', data),
  deleteEquipment: async (id) =>
    apiRequest(`${API_PREFIX}/equipment/${id}/`, 'DELETE'),
  getEquipmentById: async (id) =>
    apiRequest(`${API_PREFIX}/equipment/${id}/`),

  getEquipmentDetail: async (id) => {
    try {
      return await apiRequest(`${API_PREFIX}/equipment/${id}/detail-summary/`);
    } catch (err) {
      if (!isNotFoundError(err)) throw err;
    }

    let equipment = null;
    try {
      const single = await apiRequest(`${API_PREFIX}/equipment/${id}/`);
      equipment = single?.data ?? single;
    } catch (innerErr) {
      if (!isNotFoundError(innerErr)) throw innerErr;
      const listResponse = await apiRequest(`${API_PREFIX}/equipment/`);
      equipment = unwrapList(listResponse).find((row) => String(row.id) === String(id));
    }

    if (!equipment) {
      throw new Error('Ekipman bulunamadı.');
    }

    const recordsResponse = await apiRequest(
      actionUrl(`${API_PREFIX}/maintenance-records`, { equipment: id })
    );

    return {
      success: true,
      data: buildEquipmentDetailPayload(equipment, unwrapList(recordsResponse)),
    };
  },

  getMaintenanceRecords: async (params = {}) =>
    apiRequest(`${API_PREFIX}/maintenance-records/${buildQuery(params)}`),
  createMaintenanceRecord: async (data) =>
    apiRequest(`${API_PREFIX}/maintenance-records/`, 'POST', data),
  updateMaintenanceRecord: async (id, data) =>
    apiRequest(`${API_PREFIX}/maintenance-records/${id}/`, 'PATCH', data),
  deleteMaintenanceRecord: async (id) =>
    apiRequest(`${API_PREFIX}/maintenance-records/${id}/`, 'DELETE'),
  getMaintenanceSchedule: async (params = {}) => {
    try {
      return await apiRequest(actionUrl(`${API_PREFIX}/maintenance-records/schedule`, params));
    } catch (err) {
      if (!isNotFoundError(err)) throw err;
    }

    const recordsResponse = await apiRequest(`${API_PREFIX}/maintenance-records/`);
    return {
      success: true,
      data: buildSchedulePayload(unwrapList(recordsResponse), params),
    };
  },

  getInvoices: async (params = {}) =>
    apiRequest(`${API_PREFIX}/invoices/${buildQuery(params)}`),
  getInvoice: async (id) => apiRequest(`${API_PREFIX}/invoices/${id}/`),
  createInvoice: async (data) =>
    apiRequest(`${API_PREFIX}/invoices/`, 'POST', data),
  updateInvoice: async (id, data) =>
    apiRequest(`${API_PREFIX}/invoices/${id}/`, 'PATCH', data),
  deleteInvoice: async (id) =>
    apiRequest(`${API_PREFIX}/invoices/${id}/`, 'DELETE'),

  getAuditLogs: async (params = {}) =>
    apiRequest(`${API_PREFIX}/audit-logs/${buildQuery(params)}`),
};

export default technicalMaintenanceApi;

export const AUDIT_TABLE_OPTIONS = [
  { value: '', label: 'Tüm menüler' },
  { value: 'equipment', label: 'Ekipman Envanteri' },
  { value: 'maintenance_records', label: 'Bakım Kayıtları' },
  { value: 'maintenance_invoices', label: 'Fatura Girişi' },
];

export const AUDIT_ACTION_OPTIONS = [
  { value: '', label: 'Tüm işlemler' },
  { value: 'INSERT', label: 'Yeni kayıt' },
  { value: 'UPDATE', label: 'Güncelleme' },
  { value: 'DELETE', label: 'Silme' },
];

export const EQUIPMENT_CATEGORIES = [
  { value: 'machine_list', label: 'Makine Listesi' },
  { value: 'cold_room', label: 'Soğuk Oda' },
  { value: 'cabinet', label: 'Dolap Listesi' },
  { value: 'ice_machine', label: 'Buz Makinesi' },
];
